import { ChevronDown, Clock, Coins, Scissors, User } from "lucide-react";
import { DateTime } from "luxon";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TierBadge } from "@/components/ui/tier-badge";
import {
  type AppointmentOutcome,
  getOutcomeStatusInfo,
  OUTCOME_STATUS_VALUES,
} from "@/domains/appointment/appointments/shared/utils/appointment-status-style";
import type { CustomerTierFragment } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";
import { useWorkerActions } from "../hooks";
import { useCurrentWorker } from "../hooks/use-current-worker";
import type { WorkerAppointment } from "../types";

interface WorkerAppointmentCardProps {
  appointment: WorkerAppointment;
  showQuickActions?: boolean;
  onAppointmentClick?: (appointment: WorkerAppointment) => void;
}

export function WorkerAppointmentCard({
  appointment,
  showQuickActions = true,
  onAppointmentClick,
}: WorkerAppointmentCardProps) {
  const { startService, completeService, markNoShow } = useWorkerActions();
  const { currentWorker } = useCurrentWorker();

  // Parse appointment data
  const startTime = timezoneUtils.fromISO(appointment.startTime);
  const endTime = timezoneUtils.fromISO(appointment.endTime);
  const _duration = Math.round(endTime.diff(startTime, "minutes").minutes);

  const customerName =
    `${appointment.customer?.firstName || ""} ${appointment.customer?.lastName || ""}`.trim() ||
    "Unknown Customer";
  const customerTier = appointment.customer?.tier;

  // Enhanced service information
  const services = appointment.appointmentServices || [];

  // Get services assigned to current worker
  const myServices = services.filter((service) => service.workerId === currentWorker?.id);

  // Check if current worker has any services in this appointment
  const hasMyServices = myServices.length > 0;

  // Enhanced service timeline calculation for unified view
  const calculateUnifiedTimeline = () => {
    if (!services.length)
      return {
        timelineItems: [] as Array<{
          id: string;
          serviceName: string;
          serviceDuration: number;
          sequenceOrder: number;
          workerId: string | null;
          totalPrice?: number;
          worker?: { id: string; displayName: string } | null;
          startTime: DateTime;
          endTime: DateTime;
          isMyService: boolean;
          workerName: string;
        }>,
        myServiceStartTime: startTime,
        myServiceEndTime: endTime,
        myServiceDuration: 0,
        hasIntermediaryServices: false,
      };

    // Sort all services by sequence order
    const sortedServices = [...services].sort((a, b) => a.sequenceOrder - b.sequenceOrder);

    let currentTime = startTime;
    let myServiceStartTime: DateTime | null = null;
    let myServiceEndTime: DateTime | null = null;
    let myServiceDuration = 0;
    const timelineItems: Array<{
      id: string;
      serviceName: string;
      serviceDuration: number;
      sequenceOrder: number;
      workerId: string | null;
      totalPrice?: number;
      worker?: { id: string; displayName: string } | null;
      startTime: DateTime;
      endTime: DateTime;
      isMyService: boolean;
      workerName: string;
    }> = [];
    let hasIntermediaryServices = false;

    for (const service of sortedServices) {
      const serviceStartTime = currentTime;
      const serviceEndTime = currentTime.plus({ minutes: service.serviceDuration });
      const isMyService = service.workerId === currentWorker?.id;

      if (isMyService) {
        if (!myServiceStartTime) {
          myServiceStartTime = serviceStartTime;
        }
        myServiceEndTime = serviceEndTime;
        myServiceDuration += service.serviceDuration;
      } else {
        hasIntermediaryServices = true;
      }

      timelineItems.push({
        ...service,
        startTime: serviceStartTime,
        endTime: serviceEndTime,
        isMyService,
        workerName: service.worker ? service.worker.displayName || "Unknown Worker" : "Unassigned",
      });

      currentTime = serviceEndTime;
    }

    return {
      timelineItems,
      myServiceStartTime: myServiceStartTime || startTime,
      myServiceEndTime: myServiceEndTime || endTime,
      myServiceDuration,
      hasIntermediaryServices,
      totalDuration: Math.round(endTime.diff(startTime, "minutes").minutes),
    };
  };

  const timeline = calculateUnifiedTimeline();

  // Determine if worker should be alerted (their services starting soon)
  const isStartingSoon = () => {
    if (!hasMyServices) return false;
    const now = DateTime.now();
    const minutesUntilStart = timeline.myServiceStartTime.diff(now, "minutes").minutes;
    return minutesUntilStart <= 15 && minutesUntilStart > 0; // Alert if starting within 15 minutes
  };

  const shouldHighlight = isStartingSoon();

  // Outcome status indicators - only show confirmed appointments to workers
  const _isConfirmed = appointment.bookingStatus === "CONFIRMED";
  const currentOutcome = appointment.outcome as AppointmentOutcome | null;

  // Use shared status utility
  const outcomeStatusInfo = getOutcomeStatusInfo(currentOutcome);
  const StatusIcon = outcomeStatusInfo.icon;

  // Generic outcome action handler
  const handleOutcomeChange = (outcome: AppointmentOutcome | null) => {
    // Use original appointment ID for split appointments
    const appointmentId = appointment.originalId || appointment.id;

    // TODO: Update these to use generic outcome update action
    switch (outcome) {
      case "IN_PROGRESS":
        startService(appointmentId);
        break;
      case "COMPLETED":
        completeService(appointmentId);
        break;
      case "NO_SHOW":
        markNoShow(appointmentId);
        break;
      case "RESCHEDULED":
        break;
      case null:
        break;
    }
  };

  const handleCardClick = () => {
    onAppointmentClick?.(appointment);
  };

  const Tag = onAppointmentClick ? "button" : "div";
  return (
    <Tag
      {...(onAppointmentClick ? { type: "button" as const } : {})}
      className={`
        relative w-full text-left border rounded-lg px-4 pt-4 pb-0 transition-all duration-200
        bg-card hover:shadow-md
        ${onAppointmentClick ? "cursor-pointer" : ""}
        ${shouldHighlight ? "border-orange-400 bg-orange-50 dark:bg-orange-950/20" : "border-border"}
      `}
      onClick={handleCardClick}
    >
      {/* Header: Time and Outcome Actions */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-col">
          {/* Split appointment indicator */}
          {appointment.isConsecutiveBlock &&
            appointment.totalBlocks &&
            appointment.totalBlocks > 1 && (
              <div className="mb-1">
                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                  Part {(appointment.blockIndex || 0) + 1} of {appointment.totalBlocks}
                </Badge>
              </div>
            )}

          <div className="flex items-center gap-1">
            {!hasMyServices ? (
              // No services assigned - show appointment time
              <>
                <div className="text-xl font-bold text-muted-foreground">
                  {startTime.toFormat("HH:mm")}
                </div>
                <span className="text-sm text-muted-foreground ml-1">
                  - {endTime.toFormat("HH:mm")}
                </span>
              </>
            ) : (
              // Show worker's total service time range
              <>
                <div className="text-xl font-bold text-foreground">
                  {timeline.myServiceStartTime.toFormat("HH:mm")}
                </div>
                <span className="text-sm text-muted-foreground ml-1">
                  - {timeline.myServiceEndTime.toFormat("HH:mm")}
                </span>
              </>
            )}
          </div>
          <div className="text-xs text-muted-foreground">{startTime.toFormat("MMM d, yyyy")}</div>
        </div>

        {/* Outcome Status Dropdown - Top Right */}
        {showQuickActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full cursor-pointer hover:opacity-80 touch-manipulation min-h-[32px] ${outcomeStatusInfo.backgroundColor} ${outcomeStatusInfo.borderColor}`}
              >
                <StatusIcon className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`} />
                <span className={`text-xs font-medium ${outcomeStatusInfo.textColor}`}>
                  {outcomeStatusInfo.text}
                </span>
                <ChevronDown className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`} />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {OUTCOME_STATUS_VALUES.map((outcome) => {
                const statusInfo = getOutcomeStatusInfo(outcome);
                const StatusMenuIcon = statusInfo.icon;
                const isCurrentStatus = outcome === currentOutcome;

                return (
                  <DropdownMenuItem
                    key={outcome || "scheduled"}
                    onClick={() => handleOutcomeChange(outcome)}
                    disabled={isCurrentStatus}
                    className={isCurrentStatus ? "opacity-50" : ""}
                  >
                    <StatusMenuIcon className={`w-4 h-4 mr-2 ${statusInfo.iconColor}`} />
                    {statusInfo.text}
                    {isCurrentStatus && (
                      <span className="ml-auto text-xs text-muted-foreground">Current</span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          /* Read-only status badge when actions are disabled */
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full min-h-[32px] ${outcomeStatusInfo.backgroundColor} ${outcomeStatusInfo.borderColor}`}
          >
            <StatusIcon className={`w-3 h-3 ${outcomeStatusInfo.iconColor}`} />
            <span className={`text-xs font-medium ${outcomeStatusInfo.textColor}`}>
              {outcomeStatusInfo.text}
            </span>
          </div>
        )}
      </div>

      {/* Customer Info */}
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">{customerName}</h3>
          {customerTier && (
            <TierBadge tier={customerTier as unknown as CustomerTierFragment} size="sm" />
          )}
        </div>

        {/* Enhanced Services Section - Unified Timeline */}
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <Scissors className="w-4 h-4 text-muted-foreground mt-0.5" />
            <div className="flex-1">
              {!hasMyServices ? (
                // Worker has no services in this appointment
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">
                      No services assigned to you
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {timeline.timelineItems.map((item) => (
                      <li key={item.id} className="text-sm flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        <span className="flex-1 text-muted-foreground">{item.serviceName}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">
                            {item.serviceDuration}min
                          </span>
                          <span className="text-xs text-muted-foreground px-1">
                            {item.workerName}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                // Worker has services - show only worker's services
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Your Services</span>
                    <Badge
                      variant="default"
                      className="text-xs bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300"
                    >
                      {timeline.myServiceDuration}min
                    </Badge>
                  </div>

                  <div className="space-y-1">
                    {timeline.timelineItems
                      .filter((item) => item.isMyService)
                      .map((item) => {
                        const myServicesCount = timeline.timelineItems.filter(
                          (t) => t.isMyService
                        ).length;
                        const showServiceTimes =
                          myServicesCount > 1 || timeline.hasIntermediaryServices;

                        return (
                          <div key={item.id} className="flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            <span className="flex-1 text-foreground font-medium">
                              {item.serviceName}
                            </span>
                            <div className="flex items-center gap-1">
                              {showServiceTimes && (
                                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                  {item.startTime.toFormat("HH:mm")}-
                                  {item.endTime.toFormat("HH:mm")}
                                </span>
                              )}
                              <Badge
                                variant="default"
                                className="text-xs bg-blue-100 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 px-1 py-0"
                              >
                                You
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Starting Soon Alert */}
          {shouldHighlight && (
            <div className="bg-orange-50 dark:bg-orange-950/20 p-2 rounded border-l-2 border-orange-200 dark:border-orange-800">
              <p className="text-xs text-orange-700 dark:text-orange-300 font-medium">
                🔔 Your services start soon!
              </p>
            </div>
          )}

          {appointment.customerMessage && (
            <div className="bg-blue-50 dark:bg-blue-950/20 p-2 rounded border-l-2 border-blue-200 dark:border-blue-800">
              <p className="text-xs text-blue-700 dark:text-blue-300 italic">
                💬 "{appointment.customerMessage}"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Price and Additional Info */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="text-lg font-semibold text-foreground">
              ${(appointment.totalPrice / 100).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Duration Badge - Worker's Duration */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
          <Clock className="w-3 h-3" />
          {timeline.myServiceDuration}min
        </div>
      </div>
    </Tag>
  );
}
